import React, { useState, useEffect, useMemo } from "react";
import { Modal, Box, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Parses the DB-stored image_urls field. The backend currently writes a
// python-list-repr string ("['url1', 'url2']") as well as real JSON, so we
// try real JSON first and fall back to a tolerant parse only if needed.
export function parseImageUrls(str) {
  if (!str) return [];
  if (Array.isArray(str)) return str.filter(Boolean).map(String);
  if (typeof str !== "string") return [];

  // Real JSON first (covers ["a","b"]).
  try {
    const v = JSON.parse(str);
    if (Array.isArray(v)) return v.filter(Boolean).map(String);
  } catch {
    /* fall through */
  }

  // Tolerant parse for python-style "['a', 'b']" strings. We only swap quotes
  // if the string starts with [' to avoid mangling URLs that contain '.
  const trimmed = str.trim();
  if (trimmed.startsWith("['") || trimmed.startsWith('["')) {
    try {
      const v = JSON.parse(trimmed.replace(/'/g, '"'));
      if (Array.isArray(v)) return v.filter(Boolean).map(String);
    } catch {
      /* ignore */
    }
  }

  // Single URL fallback.
  if (trimmed.startsWith("http")) return [trimmed];
  return [];
}

const modalStyle = {
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
  outline: "none",
  width: 1000,
  height: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const arrowStyle = (side) => ({
  position: "absolute",
  top: "50%",
  [side]: 10,
  transform: "translateY(-50%)",
  zIndex: 2,
});

export default function ImageGalleryCell({ imageUrlsStr, thumbnailSize = 60 }) {
  // Memoize so the array reference is stable across re-renders — otherwise
  // the preload effect runs every render.
  const images = useMemo(() => parseImageUrls(imageUrlsStr), [imageUrlsStr]);

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const preload = (src) => {
      const img = new Image();
      img.src = src;
    };
    preload(images[(currentIndex + 1) % images.length]);
    preload(images[(currentIndex - 1 + images.length) % images.length]);
  }, [currentIndex, images]);

  if (!images.length) return null;

  return (
    <>
      <img
        src={images[0]}
        alt="thumbnail"
        loading="lazy"
        style={{
          width: thumbnailSize,
          height: thumbnailSize,
          objectFit: "cover",
          borderRadius: 4,
          cursor: "pointer",
        }}
        onClick={() => setOpen(true)}
      />
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          {images.length > 1 && (
            <IconButton
              onClick={() =>
                setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1))
              }
              sx={arrowStyle("left")}
              aria-label="Previous image"
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          <img
            src={images[currentIndex]}
            alt={`image-${currentIndex}`}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: 4,
            }}
          />

          {images.length > 1 && (
            <IconButton
              onClick={() =>
                setCurrentIndex((i) => (i === images.length - 1 ? 0 : i + 1))
              }
              sx={arrowStyle("right")}
              aria-label="Next image"
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>
      </Modal>
    </>
  );
}
